import { KonvaEventObject } from 'konva/lib/Node';
import { FunctionComponent, useMemo, useRef, useState } from 'react';
import { Group, Layer, Rect, Stage } from 'react-konva';
import { dateWithoutTime, nowWithoutTime } from 'utils/time';
import { GraphProps } from "./GraphProps";
import DateRangeCmp from './components/DateRangeCmp';
import DaySelect from './components/DaySelect';
import Header from './components/Header';
import Histogram from './components/Histogram';
import Timeline from './components/Timeline';
import TooltipDay from './components/TooltipDay';
import TooltipSum from './components/TooltipSum';
import ActivitySelect from './components/activity/ActivitySelect';
import GraphGroupCmp from './components/activity/GraphGroupCmp';
import LayoutCollection from './components/layout/LayoutCollection';
import ScrollHoriz from './components/layout/ScrollHoriz';
import ScrollVert from './components/layout/ScrollVert';
import { Activity, ROW_TYPE } from "./types";
import { groupsHeightForEach, timeFromXStyled, xFromTimeStyled } from './utils';



const today = nowWithoutTime()

const Graph: FunctionComponent<GraphProps> = ({
	readOnly,
	groups,

	activitySelect,
	activityHighlight,
	dateHighlight,

	dateStartView,
	columnsEndView,

	dateCurrent,
	dateRange = {},
	genericDateRange,

	onChangeGroups,
	onHorizontalScroll,
	onActivityHighlightChanged,
	onDateHighlightChanged,
	onClick,
	onDblClick,
	onDrag,
	onDragStart,
	onDragEnd,

	style,
}) => {
	//#region HOOK

	// id timeout per il mouseover
	const timeoutIdRef = useRef(0)
	// offset scroll vertical
	const [scrollY, setScrollY] = useState(0)
	const [scrollYByWeel, setScrollYByWeel] = useState(0)

	// calcolo l'altezza dell'intero PLANNER e di solo i blocchi ACTIVITIES
	const { actionsGroupsHeight, allGroupsHeight } = useMemo(() => {
		let actionsGroupsHeight = 0
		let allGroupsHeight = groupsHeightForEach(groups, style, (group, y) => {
			if (group.name == "task") actionsGroupsHeight = y
		})
		allGroupsHeight += ScrollHoriz.CalcHeight({ style })
		return { actionsGroupsHeight, allGroupsHeight }
	}, [groups])

	//#endregion



	//#region RENDER

	// normalizzo le date
	dateStartView = dateWithoutTime(dateStartView)
	dateCurrent = dateWithoutTime(dateCurrent)

	// non fare nulla se non ci sono dati
	if (!(groups?.length > 0) || !dateCurrent || !dateStartView || !columnsEndView) return null

	// trasformazioni specifiche per questo "style"
	const timeFromX = timeFromXStyled(style, dateCurrent)
	const xFromTime = xFromTimeStyled(style, dateCurrent)
	const rowFromY = (y: number) => y < timelineHeight ? ROW_TYPE.TIMELINE : null

	// altezza della timeline
	const timelineHeight = Timeline.CalcHeight({ style })
	// larghezza del graph (senza contare la colonna dei LAYERs)
	const areaWidth = style.width - style.stageMarginLeft
	// altezza dell'area conle ACTIVITY escluso quindi TIMELINE e SCROLL
	const areaHeight = style.height - timelineHeight - style.scroll.height
	// x dei tooltip 
	const tooltipX = xFromTime(dateRange.end ?? dateHighlight) + style.space.x

	const getPositionFromEvent = (event) => {
		if (event.type == "touchend") {
			var rect = event.target.getBoundingClientRect();
			var x = event.changedTouches[0].pageX - rect.left;
			var y = event.changedTouches[0].pageY - rect.top;
			return { x, y }
		} else {
			return { x: event.offsetX, y: event.offsetY }
		}
	}

	//#endregion


	//#region EVENTS

	/** vado sopra un ACTIVITY col MOUSE */
	const handleActivityEnter = (e: KonvaEventObject<MouseEvent>, activity: Activity) => {
		if (!activity) return

		//const res = findActivity(activities, activity)
		onActivityHighlightChanged?.(e.evt, activity)
	}
	/** esco dall'area di un ACTIVITY col MOUSE */
	const handleActivityLeave = (e: KonvaEventObject<MouseEvent>, activity: Activity) => {
		onActivityHighlightChanged?.(e.evt, null)
	}

	/** clicco su un ACTIVITY */
	const handleActivityClick = (e: KonvaEventObject<MouseEvent>, activity: Activity) => {
		const { x } = getPositionFromEvent(e.evt)
		const dateSelect = timeFromX(x)
		onClick?.({ event: e.evt, dateSelect, row: ROW_TYPE.ACTIVITY, activity })
	}
	/** clicco sul BG (quindi in qualunque punto che NON sia un ACTIVITY) */
	const handleClickStage = (e: KonvaEventObject<MouseEvent>) => {
		const { x, y } = getPositionFromEvent(e.evt)
		const dateSelect = timeFromX(x)
		const row = rowFromY(y)
		onClick?.({ event: e.evt, dateSelect, row, activity: null })
	}
	/** doppioclicco su un ACTIVITY */
	const handleDblClickActivity = (e: KonvaEventObject<MouseEvent>, activity: Activity) => {
		const { x } = getPositionFromEvent(e.evt)
		const dateSelect = timeFromX(x)
		onDblClick?.({ event: e.evt, dateSelect, row: ROW_TYPE.ACTIVITY, activity: activity })
	}
	/** doppioclicco su BG */
	const handleDblClickStage = (e: KonvaEventObject<MouseEvent>) => {
		const { x, y } = getPositionFromEvent(e.evt)
		const dateSelect = timeFromX(x)
		const row = rowFromY(y)
		onDblClick?.({ event: e.evt, dateSelect, row, activity: null })
	}

	/**
	 * il mouse si muove nello STAGE 
	 * mi serve per evidenziare il giorno in highlight
	 * visualizzare le tooltip
	 */
	const handleMouseMoveStage = e => {
		e.cancelBubble = true
		const { x } = getPositionFromEvent(e.evt)
		const dateMouseOver = timeFromX(x)
		if (dateHighlight == dateMouseOver) return
		if (timeoutIdRef.current != 0) return

		timeoutIdRef.current = window.setTimeout(() => {
			onDateHighlightChanged?.(dateMouseOver)
			// quindo mi muovo col mouse devo visualizzare i tooltip
			//if (dateRange.start && !dateRange.end) calcTooltipValues(dateRange.start, dateMouseOver)
			timeoutIdRef.current = 0
		}, 15)
	}
	/**
	 * il mouse esce dallo STAGE
	 */
	const handleMouseLeave = e => {
		onDateHighlightChanged?.(null)
	}
	/**
	 * è cambiato qualcosa nei GROUPS-BG
	 * cioe' il COLLAPSED
	 */
	const handleGroupChange = (bgNew, index) => {
		groups[index] = bgNew // aiaiai non si fa!!!!
		onChangeGroups?.([...groups])
	}
	/**
	 * Scroll verticale
	 */
	const handleScrollY = (y) => {
		setScrollY(y)
	}
	/**
	 * Scroll verticale con la rotellina
	 */
	const handleWeel = (e) => {
		setScrollYByWeel(scrollY + style.space.y * (e.evt.deltaY > 0 ? 2 : -2))
	}

	//#endregion


	// RENDER
	return (
		<Stage
			style={{ backgroundColor: style.stageColor }}
			width={style.width}
			height={style.height}
			onClick={handleClickStage}
			onTap={handleClickStage}
			onDblClick={handleDblClickStage}
			onMouseMove={handleMouseMoveStage}
			onMouseLeave={handleMouseLeave}
			onWheel={handleWeel}
		>
			<Layer x={style.stageMarginLeft} y={0}
			//	imageSmoothingEnabled={ false}
			>
				{/* BACKGROUND */}
				<Rect listening={false}
					x={0} y={timelineHeight}
					width={areaWidth} height={style.height - timelineHeight}
					fill={style.bgColor}
				/>

				<Timeline
					x={0.5} y={0.5}
					start={dateCurrent}
					days={style.columns}
					style={style}
				/>

				{/* CONTENTs */}
				<Group
					y={timelineHeight}
					clip={{
						x: -style.stageMarginLeft,
						y: 0,
						width: style.width,
						height: areaHeight - 5
					}}
				>
					<LayoutCollection y={-scrollY} height={areaHeight}>
						{groups.map((group, index) => (
							<GraphGroupCmp key={index}
								group={group}
								start={dateCurrent}
								style={style}
								activityHighlight={activityHighlight}
								activitySelect={activitySelect}
								areaHeight={areaHeight}

								onGroupChange={bgNew => handleGroupChange(bgNew, index)}
								onActivityEnter={handleActivityEnter}
								onActivityLeave={handleActivityLeave}
								onActivityClick={handleActivityClick}
								onActivityDoubleClick={handleDblClickActivity}

								HeaderRender={group => {
									if (!group.activitiesHistogram) return null
									return <Histogram
										x={0} y={-Header.CalcHeight({ style })}
										width={areaWidth} height={style.histogram.height}
										start={dateCurrent}
										activities={group.activitiesHistogram}
										style={style}
									/>
								}}
								RenderLeaf={(group) => <TooltipSum
									dateRange={dateRange}
									group={group}
									style={style}
									x={tooltipX} y={0}
								/>}
							/>
						))}
					</LayoutCollection>
				</Group>


				{/* ACTIVE AND SELECT COLUMNS */}
				<Group
					x={-.5} y={-.5}
					clip={{ x: 0, y: 0, width: areaWidth, height: style.height }}
				>

					{/* MIDDLE */}
					{/* Il giorno di oggi */}
					<DaySelect
						start={dateCurrent}
						select={today}
						y={style.space.y - .5}
						height={areaHeight + style.space.y}
						fill={style.day.current.color}
						stroke={style.day.current.border}
						strokeWidth={style.day.current.thickness}
						opacity={style.day.current.opacity}
						style={style}
					/>

					{/* Il Timelinegiorno sotto al mouse */}
					<DaySelect
						start={dateCurrent}
						select={dateHighlight}
						y={style.space.y - .5}
						height={areaHeight + style.space.y}
						fill={style.day.highlight.color}
						stroke={style.day.highlight.border}
						strokeWidth={style.day.highlight.thickness}
						opacity={style.day.highlight.opacity}
						style={style}
					/>

					{/* SELECTION */}
					<DateRangeCmp
						start={dateCurrent}
						range={dateRange}
						height={areaHeight + style.space.y}
						style={style}
					/>

					{/* GENERICDATARANGE */}
					<DateRangeCmp
						start={dateCurrent}
						range={genericDateRange}
						height={Math.min(areaHeight, allGroupsHeight) + style.space.y}
						style={style}
						styleRange={{
							stroke: "#FFF",
							fill: "transparent",
							mUp: style.space.y + 3.5,
							mLeft: 2.5, mRight: 2.5, mBottom: 2.5,
							strokeWidth: 2,
							dash: [5, 5],
						}}
					/>
				</Group>

				{/* Disegna le righe dell'activity selezionata */}
				<ActivitySelect
					readOnly={readOnly}
					dateStart={dateStartView}
					dateCurrent={dateCurrent}
					activitiesSelect={activitySelect}
					groups={groups}
					onChangeGroups={onChangeGroups}
					onDrag={onDrag}
					onDragStart={onDragStart}
					onDragEnd={onDragEnd}

					x={-.5} y={timelineHeight - .5}
					clip={{ x: 0, y: 0, width: areaWidth, height: style.height }}
					height={actionsGroupsHeight}
					scrollY={-scrollY}

					style={style}
				/>

				{/* POPUP DAY*/}
				<TooltipDay
					dateRange={dateRange}
					style={style}
					x={tooltipX}
					y={style.space.y}
				/>

				{/* SCROLLBAR */}
				<ScrollHoriz
					x={0} y={style.height - style.scroll.height}
					width={areaWidth}
					height={style.scroll.height}

					start={dateStartView}
					daysTot={columnsEndView}
					initDate={dateCurrent}

					onChange={onHorizontalScroll}
					style={style}
				/>
				<ScrollVert
					x={areaWidth - style.scroll.width} y={timelineHeight}
					width={style.scroll.width}
					height={areaHeight - 5}

					heightTot={allGroupsHeight}

					onChange={handleScrollY}
					scrollYByWeel={scrollYByWeel}
					style={style}
				/>

			</Layer>

		</Stage>
	)
}

export default Graph